import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { Button } from './ui/button';
import { FileText, Zap, Download, ArrowRight } from 'lucide-react';
import { sanitizeHtml, isHtmlLike } from '../lib/sanitizeHtml';
import { loadSiteContentPage } from '../lib/siteContent';

function normalizeHeroHeadingHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/\r\n|\r/g, '\n')
    // Convert common block wrappers to line breaks (headings should be mostly inline + <br/>).
    .replace(/<\s*\/\s*(div|p)\s*>\s*<\s*(div|p)(\s+[^>]*)?>/gi, '<br />')
    .replace(/<\s*(div|p)(\s+[^>]*)?>/gi, '')
    .replace(/<\s*\/\s*(div|p)\s*>/gi, '')
    .trim();
}

function Hero() {
  const [content, setContent] = useState({
    main_heading: 'Small PDFs.\nBig Results.',
    subheading: 'Tiny, 2-page PDFs packed with actionable insights you can read in under 2 minutes—100% free to download.',
    bullet_points: ['Concise, expert-crafted guides', 'Actionable steps you can implement today', 'No sign-up required for free downloads'],
    trust_text: 'TinyPDFs was built for creators, entrepreneurs, and lifelong learners who want fast, focused knowledge without the fluff.',
    cta_primary: 'Browse Free Library',
    cta_secondary: 'Discover Premium',
    trust_indicator: 'Built for creators, entrepreneurs, and lifelong learners'
  });

  const [contentTypeByKey, setContentTypeByKey] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const items = await loadSiteContentPage('home_hero');
      const contentMap = {};
      const typeMap = {};
      
      items.forEach(item => {
        const normalizedType = String(item.content_type || '').toLowerCase();
        const inferredType = (!normalizedType || normalizedType === 'text') && isHtmlLike(item.value) ? 'html' : normalizedType;
        typeMap[item.key] = inferredType;
        if (item.key === 'bullet_points') {
          if (item.content_type === 'json') {
            contentMap[item.key] = JSON.parse(item.value);
          } else {
            try {
              contentMap[item.key] = JSON.parse(item.value);
            } catch {
              // ignore parse failure and fall back to defaults
            }
          }
          return;
        }

        contentMap[item.key] = item.value;
      });

      // Backwards/alternate key support (for seeded content)
      if (contentMap.title && !contentMap.main_heading) contentMap.main_heading = contentMap.title;
      if (contentMap.subtitle && !contentMap.subheading) contentMap.subheading = contentMap.subtitle;
      if (contentMap.trust && !contentMap.trust_indicator) contentMap.trust_indicator = contentMap.trust;
      if (contentMap.trustline && !contentMap.trust_indicator) contentMap.trust_indicator = contentMap.trustline;

      if (typeMap.title && !typeMap.main_heading) typeMap.main_heading = typeMap.title;
      if (typeMap.subtitle && !typeMap.subheading) typeMap.subheading = typeMap.subtitle;
      
      setContent(prev => ({ ...prev, ...contentMap }));
      setContentTypeByKey(prev => ({ ...prev, ...typeMap }));
    } catch (error) {
      console.error('Error fetching hero content:', error);
    }
  };

  const bulletPoints = [
    { icon: FileText, text: content.bullet_points[0] || 'Concise, expert-crafted guides' },
    { icon: Zap, text: content.bullet_points[1] || 'Actionable steps you can implement today' },
    { icon: Download, text: content.bullet_points[2] || 'No sign-up required for free downloads' },
  ];

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-background pt-32 pb-20"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-200 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Side - Content */}
          <div className="space-y-8 text-center md:text-left">
            <div>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.1] pb-1 mb-6 whitespace-pre-line bg-clip-text text-transparent bg-gradient-to-r from-foreground to-muted-foreground"
                data-testid="hero-title"
              >
                {contentTypeByKey.main_heading === 'html' ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(normalizeHeroHeadingHtml(content.main_heading))
                    }}
                  />
                ) : (
                  content.main_heading
                )}
              </h1>
              <p className="text-lg sm:text-xl max-w-2xl mx-auto md:mx-0 text-muted-foreground mb-8">
                {contentTypeByKey.subheading === 'html' ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.subheading) }}
                  />
                ) : (
                  content.subheading
                )}
              </p>
            </div>

            {/* Bullet Points */}
            <div className="space-y-4 inline-block text-left">
              {bulletPoints.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="p-1.5 bg-orange-100 rounded-full">
                    <item.icon className="w-5 h-5 text-orange-500" />
                  </div>
                  <p className="text-base text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link to="library" smooth={true} duration={500} offset={-80}>
                <Button
                  size="lg"
                  className="w-full sm:w-auto text-base px-8 py-6 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-lg transition-transform transform hover:scale-105"
                  data-testid="browse-library-btn"
                >
                  {content.cta_primary}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="premium" smooth={true} duration={500} offset={-80}>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto text-base px-8 py-6 rounded-full border-2 border-border hover:bg-accent text-foreground transition-colors"
                  data-testid="discover-premium-btn"
                >
                  {content.cta_secondary}
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Side - Visual */}
          <div className="relative mt-12 md:mt-0">
            <div className="relative w-full aspect-square max-w-lg mx-auto">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="absolute w-full h-full rounded-full bg-gradient-to-tr from-orange-200 via-blue-200 to-purple-200 dark:from-orange-500/20 dark:via-blue-500/15 dark:to-purple-500/15 motion-safe:animate-spin-slow filter blur-3xl"></div>

                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute -top-6 right-2 w-72 h-72 rounded-full bg-gradient-to-r from-orange-200/70 via-blue-200/60 to-purple-200/70 dark:from-orange-500/25 dark:via-blue-500/20 dark:to-purple-500/25 blur-2xl opacity-70 motion-safe:animate-gradient-x [background-size:200%_200%]"></div>
                </div>

                <div className="relative w-[320px] sm:w-[420px] h-[320px] sm:h-[420px] motion-safe:animate-float">
                  <svg
                    className="w-full h-full block dark:hidden"
                    viewBox="0 0 640 640"
                    fill="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <filter id="heroDocShadow" x="-60" y="-60" width="760" height="760" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                        <feDropShadow dx="0" dy="14" stdDeviation="18" floodColor="#0F172A" floodOpacity="0.14" />
                      </filter>
                      <linearGradient id="heroBlue" x1="180" y1="120" x2="520" y2="430" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#3B82F6" />
                        <stop offset="1" stopColor="#2563EB" />
                      </linearGradient>
                      <linearGradient id="heroAmber" x1="120" y1="170" x2="440" y2="470" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#FBBF24" />
                        <stop offset="1" stopColor="#F59E0B" />
                      </linearGradient>
                    </defs>

                    <ellipse cx="230" cy="270" rx="220" ry="170" fill="#DBEAFE" opacity="0.55" />
                    <ellipse cx="430" cy="310" rx="220" ry="170" fill="#E0E7FF" opacity="0.45" />

                    <circle cx="126" cy="270" r="28" stroke="#3B82F6" strokeWidth="6" opacity="0.35" />
                    <circle cx="126" cy="270" r="12" stroke="#3B82F6" strokeWidth="6" opacity="0.35" />
                    <circle cx="168" cy="168" r="7" fill="#3B82F6" opacity="0.55" />

                    <g filter="url(#heroDocShadow)">
                      <rect x="154" y="88" width="340" height="420" rx="20" fill="#FFFFFF" transform="rotate(-10 154 88)" opacity="0.92" />
                      <rect x="132" y="120" width="340" height="420" rx="20" fill="#FFFFFF" transform="rotate(-3 132 120)" opacity="0.85" />

                      <g>
                        <rect x="192" y="96" width="316" height="380" rx="18" fill="#FFFFFF" />
                        <rect x="214" y="124" width="272" height="56" rx="10" fill="#3B82F6" opacity="0.95" />
                        <rect x="214" y="200" width="62" height="248" rx="10" fill="#3B82F6" opacity="0.95" />

                        <rect x="290" y="206" width="170" height="10" rx="5" fill="#CBD5E1" />
                        <rect x="290" y="226" width="150" height="10" rx="5" fill="#E2E8F0" />
                        <rect x="290" y="256" width="200" height="10" rx="5" fill="#E2E8F0" />
                        <rect x="290" y="276" width="184" height="10" rx="5" fill="#E2E8F0" />
                        <rect x="290" y="306" width="206" height="10" rx="5" fill="#E2E8F0" />
                        <rect x="290" y="326" width="178" height="10" rx="5" fill="#E2E8F0" />
                        <rect x="290" y="356" width="206" height="10" rx="5" fill="#E2E8F0" />

                        <rect x="370" y="206" width="120" height="78" rx="12" fill="#EFF6FF" stroke="#60A5FA" strokeWidth="2" />
                        <path d="M382 266c14-14 26-18 38-12 12 6 20 6 28 0 9-6 22-6 38 12v6H382v-6Z" fill="#3B82F6" opacity="0.65" />
                        <circle cx="462" cy="228" r="10" fill="#3B82F6" opacity="0.55" />

                        <path d="M468 96h40v40c-22 0-40-18-40-40Z" fill="#F1F5F9" />
                        <path d="M468 96h40v40c-22 0-40-18-40-40Z" stroke="#CBD5E1" strokeWidth="2" />
                        <path d="M468 96l40 40" stroke="#CBD5E1" strokeWidth="2" opacity="0.8" />
                      </g>
                    </g>

                    <g
                      className="motion-safe:animate-spin-slow"
                      style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
                    >
                      <circle cx="520" cy="270" r="64" stroke="#3B82F6" strokeWidth="6" strokeDasharray="10 12" opacity="0.55" />
                    </g>

                    <g>
                      <circle cx="590" cy="205" r="34" fill="#34D399" opacity="0.95" />
                      <path d="M590 189v32" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round" />
                      <path d="M574 205h32" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round" />
                      <path d="M560 232c10 8 20 12 30 12 10 0 20-4 30-12" stroke="#0F766E" strokeOpacity="0.25" strokeWidth="4" strokeLinecap="round" />
                    </g>

                    <circle cx="124" cy="214" r="10" fill="#60A5FA" opacity="0.6" />
                    <circle cx="116" cy="336" r="6" fill="#3B82F6" opacity="0.35" />
                    <circle cx="512" cy="430" r="6" fill="#3B82F6" opacity="0.25" />
                  </svg>

                  <svg
                    className="w-full h-full hidden dark:block"
                    viewBox="0 0 640 640"
                    fill="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <filter id="heroDocShadowDark" x="-60" y="-60" width="760" height="760" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                        <feDropShadow dx="0" dy="18" stdDeviation="22" floodColor="#000000" floodOpacity="0.45" />
                      </filter>
                      <linearGradient id="heroBlueDark" x1="180" y1="120" x2="520" y2="430" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#1D4ED8" />
                        <stop offset="1" stopColor="#1E40AF" />
                      </linearGradient>
                      <linearGradient id="heroAmberDark" x1="120" y1="170" x2="440" y2="470" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#F59E0B" />
                        <stop offset="1" stopColor="#D97706" />
                      </linearGradient>
                    </defs>

                    <ellipse cx="230" cy="270" rx="220" ry="170" fill="#1E3A8A" opacity="0.25" />
                    <ellipse cx="430" cy="310" rx="220" ry="170" fill="#312E81" opacity="0.22" />

                    <circle cx="126" cy="270" r="28" stroke="#60A5FA" strokeWidth="6" opacity="0.28" />
                    <circle cx="126" cy="270" r="12" stroke="#60A5FA" strokeWidth="6" opacity="0.22" />
                    <circle cx="168" cy="168" r="7" fill="#60A5FA" opacity="0.4" />

                    <g filter="url(#heroDocShadowDark)">
                      <rect x="154" y="88" width="340" height="420" rx="20" fill="url(#heroBlueDark)" transform="rotate(-10 154 88)" opacity="0.9" />
                      <rect x="132" y="120" width="340" height="420" rx="20" fill="url(#heroAmberDark)" transform="rotate(-3 132 120)" opacity="0.82" />

                      <g>
                        <rect x="192" y="96" width="316" height="380" rx="18" fill="#0B1220" stroke="#334155" strokeWidth="2" />
                        <rect x="214" y="124" width="272" height="56" rx="10" fill="#3B82F6" opacity="0.9" />
                        <rect x="214" y="200" width="62" height="248" rx="10" fill="#3B82F6" opacity="0.9" />

                        <rect x="290" y="206" width="170" height="10" rx="5" fill="#475569" opacity="0.8" />
                        <rect x="290" y="226" width="150" height="10" rx="5" fill="#334155" opacity="0.9" />
                        <rect x="290" y="256" width="200" height="10" rx="5" fill="#334155" opacity="0.9" />
                        <rect x="290" y="276" width="184" height="10" rx="5" fill="#334155" opacity="0.9" />
                        <rect x="290" y="306" width="206" height="10" rx="5" fill="#334155" opacity="0.9" />
                        <rect x="290" y="326" width="178" height="10" rx="5" fill="#334155" opacity="0.9" />
                        <rect x="290" y="356" width="206" height="10" rx="5" fill="#334155" opacity="0.9" />

                        <rect x="370" y="206" width="120" height="78" rx="12" fill="#0B1220" stroke="#60A5FA" strokeWidth="2" opacity="0.95" />
                        <path d="M382 266c14-14 26-18 38-12 12 6 20 6 28 0 9-6 22-6 38 12v6H382v-6Z" fill="#3B82F6" opacity="0.55" />
                        <circle cx="462" cy="228" r="10" fill="#3B82F6" opacity="0.5" />

                        <path d="M468 96h40v40c-22 0-40-18-40-40Z" fill="#0F172A" />
                        <path d="M468 96h40v40c-22 0-40-18-40-40Z" stroke="#334155" strokeWidth="2" />
                        <path d="M468 96l40 40" stroke="#334155" strokeWidth="2" opacity="0.8" />
                      </g>
                    </g>

                    <g className="motion-safe:animate-spin-slow" style={{ transformBox: 'fill-box', transformOrigin: 'center' }}>
                      <circle cx="520" cy="270" r="64" stroke="#60A5FA" strokeWidth="6" strokeDasharray="10 12" opacity="0.35" />
                    </g>

                    <g>
                      <circle cx="590" cy="205" r="34" fill="#10B981" opacity="0.7" />
                      <path d="M590 189v32" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round" opacity="0.9" />
                      <path d="M574 205h32" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round" opacity="0.9" />
                      <path d="M560 232c10 8 20 12 30 12 10 0 20-4 30-12" stroke="#0F766E" strokeOpacity="0.3" strokeWidth="4" strokeLinecap="round" />
                    </g>

                    <circle cx="124" cy="214" r="10" fill="#60A5FA" opacity="0.35" />
                    <circle cx="116" cy="336" r="6" fill="#60A5FA" opacity="0.25" />
                    <circle cx="512" cy="430" r="6" fill="#60A5FA" opacity="0.2" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Trust Indicator */}
        <div className="mt-24 text-center">
          <p className="text-sm text-muted-foreground tracking-wider uppercase">
            {content.trust_indicator}
          </p>
        </div>
      </div>
    </section>
  );
}

export default Hero;
